import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  IconButton,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation } from '@apollo/client';

interface EditDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  device: any;
  refetch: () => void;
}

const EDIT_DEVICE = gql`
  mutation EditDevice($imei: String!, $tcuId: String!) {
    editDevice(imei: $imei, tcu_id: $tcuId) {
      message
      status
    }
  }
`;

const EditDeviceDialog: React.FC<EditDeviceDialogProps> = ({ open, onClose, onSave, device, refetch }) => {
  const [form, setForm] = useState(device || {});
  const [originalValues, setOriginalValues] = useState(device || {});
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [imeiError, setImeiError] = useState<string | null>(null);
  const hasChanges = form.imei !== originalValues.imei || form.tcuId !== originalValues.tcuId;

  useEffect(() => {
    setForm(device || {});
    setOriginalValues(device || {});
  }, [device]);

  const [editDevice, { loading }] = useMutation(EDIT_DEVICE, {
    onCompleted: (data) => {
      const { message, status } = data.editDevice;

      // Check if values have actually changed
      const hasChanges = form.imei !== originalValues.imei || form.tcuId !== originalValues.tcuId;

      if (hasChanges) {
        setSnackbar({ message, severity: 'success' });
        if (data.editDevice.message === 'Device updated successfully.') {
          refetch();
          onSave();
        }
      } else {
        setSnackbar({ message: 'No changes were made.', severity: 'info' });
      }
    },
    onError: (err) => {
      console.error('Error editing device:')
      setSnackbar({ message: err.message, severity: 'error' });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Remove all whitespace from input value
    const trimmedValue = value.replace(/[^a-zA-Z0-9]/g, '');
    setForm({ ...form, [name]: trimmedValue });

    // Validate IMEI length when changed
    if (name === 'imei') {
      if (trimmedValue && (trimmedValue.length < 10 || trimmedValue.length > 15)) {
        setImeiError('IMEI must be between 10-15 characters');
      } else {
        setImeiError(null);
      }
    }
  };

  const handleSave = () => {
    // Validate IMEI length before saving
    if (form.imei && (form.imei.length < 10 || form.imei.length > 15)) {
      setSnackbar({ message: 'IMEI must be between 10-15 characters', severity: 'error' });
      return;
    }

    if (form.imei && form.tcuId) {
      editDevice({ variables: { imei: form.imei, tcuId: form.tcuId } });
    } else {
      setSnackbar({ message: 'IMEI and TCU ID are required.', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(null);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '95vw', sm: 500, md: 700 },
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          Edit Device Details
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ ml: 1, color: 'inherit', position: 'absolute', right: 15, top: 15 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid sx={{xs:12,sm:6}}>
              <TextField
                label="Device Serial No"
                name="serial"
                disabled
                value={form.serialNumber || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                inputProps={{
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
              <TextField
                disabled
                label="Device Make"
                name="make"
                value={form.tcuMake || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                inputProps={{
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
              <TextField
                disabled
                label="Device Model"
                name="model"
                value={form.tcuModel || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                inputProps={{
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
              <TextField
                label="IMEI Number"
                name="imei"
                value={form.imei || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                required
                error={!!imeiError}
                helperText={imeiError}
                inputProps={{
                  maxLength: 15,
                  minLength: 10,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined" disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="error" 
            disabled={loading || !!imeiError || !hasChanges}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar?.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        ContentProps={{
          sx: {
            backgroundColor: snackbar?.severity === 'success' ? 'green' :
              snackbar?.severity === 'error' ? 'red' : 'info.main',
            color: '#fff',
          },
        }}
      />
    </>
  );
};

export default EditDeviceDialog;